import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './configuration/theme';
import { LanguageProvider } from './shared/contexts/LanguageContext';
import { ThemeProvider, useThemeMode } from './shared/contexts/ThemeContext';
import { CookieConsentProvider } from './adapters/primary/CookieConsentContext';
import { Layout } from './adapters/primary/Layout';
import { HomePage } from './adapters/primary/HomePage';
import { OrganisationPage } from './adapters/primary/OrganisationPage';
import { MitmachenPage } from './adapters/primary/MitmachenPage';
import { InstagramPage } from './adapters/primary/InstagramPage';
import { PodcastPage } from './adapters/primary/PodcastPage';
import { ImpressumPage } from './adapters/primary/ImpressumPage';
import { PrivacyPage } from './adapters/primary/PrivacyPage';
import { CookiePage } from './adapters/primary/CookiePage';
import { CookieSettingsPage } from './adapters/primary/CookieSettingsPage';
import { CookieBanner } from './adapters/primary/CookieBanner';
import { LanguageRedirect } from './adapters/primary/LanguageRedirect';
import { ScrollToTop } from './adapters/primary/ScrollToTop';
import { GoogleAnalytics } from './adapters/primary/GoogleAnalytics';
import { CmsPageView } from './adapters/primary/CmsPageView';
import { DEFAULT_LANGUAGE, LANGUAGES, ROUTES } from './shared/constants/routes';
import { PageUseCase } from './corelogic/usecases/PageUseCase';
import { ContentUseCase, ExtendablePage } from './corelogic/usecases/ContentUseCase';

// Resolves the component names in ROUTES, which stays free of imports so that
// scripts/prerender.js can load it at build time.
const pageUseCase = new PageUseCase();
const contentUseCase = new ContentUseCase();

const PAGE_COMPONENTS: Record<string, React.ReactElement> = {
  HomePage: <HomePage />,
  OrganisationPage: <OrganisationPage />,
  MitmachenPage: <MitmachenPage />,
  InstagramPage: <InstagramPage />,
  PodcastPage: <PodcastPage />,
  ImpressumPage: <ImpressumPage />,
  PrivacyPage: <PrivacyPage />,
  CookiePage: <CookiePage />,
  CookieSettingsPage: <CookieSettingsPage />,
};

const pageElement = (component: string): React.ReactElement => {
  const element = PAGE_COMPONENTS[component];
  if (!element) {
    throw new Error(`No component registered for "${component}" - see ROUTES in routes.ts`);
  }
  return element;
};

/**
 * Hand-written pages and pages assembled in the CMS end up in one list, so the
 * bare paths, the language-prefixed paths and the catch-all always agree on
 * which URLs exist. scripts/prerender.js builds the same list for its output.
 */
const PAGE_ROUTES: Array<{ path: string; element: React.ReactElement }> = [
  // A page switched off in the editing interface gets no route, so its URL
  // falls through to the catch-all and lands on the start page rather than on
  // a page that is not supposed to exist.
  ...ROUTES.filter(
    (route) => !route.page || contentUseCase.isPublished(route.page as ExtendablePage)
  ).map((route) => ({ path: route.path, element: pageElement(route.component) })),
  ...pageUseCase.getPages().map((page) => ({
    path: page.path,
    element: <CmsPageView slug={page.slug} />,
  })),
];

// Inner component that has access to theme mode
const AppContent: React.FC = () => {
  const { mode } = useThemeMode();
  const theme = React.useMemo(() => createAppTheme(mode), [mode]);

  return (
    <MuiThemeProvider theme={theme}>
      <CssBaseline />
      <CookieConsentProvider>
        <Router>
          <LanguageProvider>
            <GoogleAnalytics />
            <ScrollToTop />
            <Layout>
              <Routes>
                {/* The default language sits on the bare paths, every other
                    language under its own prefix. Both get their own meta tags
                    and hreflang from scripts/prerender.js. */}
                {PAGE_ROUTES.map((route) => (
                  <Route key={route.path} path={route.path} element={route.element} />
                ))}
                {LANGUAGES.filter((lang) => lang !== DEFAULT_LANGUAGE).map((lang) => (
                  <Route key={lang} path={`/${lang}`}>
                    {PAGE_ROUTES.map((route) =>
                      route.path === '/' ? (
                        <Route key={route.path} index element={route.element} />
                      ) : (
                        <Route
                          key={route.path}
                          path={route.path.slice(1)}
                          element={route.element}
                        />
                      )
                    )}
                  </Route>
                ))}
                <Route path="*" element={<LanguageRedirect />} />
              </Routes>
            </Layout>
            <CookieBanner />
          </LanguageProvider>
        </Router>
      </CookieConsentProvider>
    </MuiThemeProvider>
  );
};

function App() {
  return (
    // Whether the dark view is offered is an editorial decision, made at /admin
    // and read here rather than inside the provider, which has no business
    // reaching into the content.
    <ThemeProvider darkModeEnabled={contentUseCase.isDarkModeEnabled()}>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
